import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Building2, Calendar, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import ApplicationDetailContent from '../components/ApplicationDetailContent';
import { useAuthStore } from '../stores/authStore';

interface ApplicationListItem {
  id: string;
  status: 'PENDING' | 'WITHDRAWN' | 'REVIEWED' | 'ACCEPTED' | 'REJECTED';
  created_at: string;
  updated_at: string;
  job_posting_id: string;
  resume_snapshot: any;
  job_posting: {
    id: string;
    title: string;
    position: string;
    employment_type: string;
    status: string;
    deadline: string | null;
    company_name: string | null;
    employer_address: string | null;
    salary_type: string | null;
    salary_min: number | null;
    salary_max: number | null;
  } | null;
}

const STATUS_LABELS: Record<string, { text: string; color: string }> = {
  PENDING: { text: '대기 중', color: 'bg-blue-100 text-blue-700' },
  REVIEWED: { text: '열람됨', color: 'bg-indigo-100 text-indigo-700' },
  ACCEPTED: { text: '합격', color: 'bg-green-100 text-green-700' },
  REJECTED: { text: '불합격', color: 'bg-red-100 text-red-700' },
};

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: '정규직',
  CONTRACT: '계약직',
  PART_TIME: '파트타임',
  FREELANCE: '프리랜서',
};

export default function MyApplicationsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [applications, setApplications] = useState<ApplicationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchApplications();
  }, [isAuthenticated, navigate]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/applications', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('지원 목록을 불러오지 못했습니다.');
      }
      const data = await response.json();
      setApplications(data.applications || []);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      alert('지원 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (id: string, jobTitle: string) => {
    if (!confirm(`"${jobTitle}" 공고 지원을 취소하시겠습니까?`)) return;
    setWithdrawingId(id);
    try {
      const response = await fetch(`/api/v1/applications/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '지원 취소에 실패했습니다.');
      }
      setApplications(prev => prev.filter(a => a.id !== id));
      setExpandedId(null);
    } catch (err: any) {
      alert(err.message || '지원 취소에 실패했습니다.');
    } finally {
      setWithdrawingId(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          <AdminSidebar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">로딩 중...</p>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Briefcase className="w-7 h-7 text-primary" />
                지원 목록
              </h1>
              <p className="text-gray-600">지원한 채용 공고를 확인하고 관리할 수 있습니다.</p>
            </div>

            {applications.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">지원한 공고가 없습니다</h3>
                <p className="text-gray-600 mb-6">관심있는 채용 공고에 지원해보세요!</p>
                <button
                  onClick={() => navigate('/?tab=jobs')}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  채용 공고 보러가기
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map(app => {
                  const job = app.job_posting;
                  if (!job) return null;
                  const isExpanded = expandedId === app.id;
                  const statusInfo = STATUS_LABELS[app.status];

                  return (
                    <div
                      key={app.id}
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div
                        className="p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedId(isExpanded ? null : app.id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {statusInfo && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                  {statusInfo.text}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                {EMPLOYMENT_TYPE_LABELS[job.employment_type] || job.employment_type}
                              </span>
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-1">{job.title}</h3>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              {job.company_name && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3.5 h-3.5 text-gray-400" />
                                  {job.company_name}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                지원: {formatDate(app.created_at)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWithdraw(app.id, job.title);
                              }}
                              disabled={withdrawingId === app.id}
                              className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {withdrawingId === app.id ? '취소 중...' : '지원 취소'}
                            </button>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-100 p-5 bg-gray-50/50">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">지원 시 제출한 이력서</h4>
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <ApplicationDetailContent snapshot={app.resume_snapshot} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
